import type { ReactNode } from 'react';
import { Button } from './Button';
import { Modal } from './Modal';

/** The body on the settings pages (App Settings, Billing Admin, Site Integrations): spec:307. */
export const UNSAVED_SECTION_MESSAGE =
  "This section has changes that haven't been saved. Save them before leaving, or discard them.";

/**
 * "Unsaved changes": asked before leaving a section or a page whose changes are not saved yet (spec
 * "Saving and unsaved changes"). Three ways out, in this order:
 * - Keep editing: stay; nothing changes. Escape, the backdrop and the ✕ do the same.
 * - Discard changes: the caller restores the last saved values, then leaves.
 * - Save and continue: the caller saves, then leaves only if the save worked. A failed save keeps
 *   the person where they were, with the card's own error on screen.
 *
 * Built on the shared Modal, with its focus trap. While `saving`, nothing but the save can happen: the
 * other buttons are disabled and Escape, the backdrop and the ✕ do nothing. `canSave` false (a change
 * that cannot be saved as it stands, such as a number out of range) disables Save and continue only.
 * `returnFocusTo` is where focus goes when it closes: usually the tab or link that asked to leave.
 */
export function UnsavedChangesDialog({
  open,
  message = UNSAVED_SECTION_MESSAGE,
  saving = false,
  canSave = true,
  onKeepEditing,
  onDiscard,
  onSave,
  returnFocusTo,
}: {
  open: boolean;
  /** The body text. Defaults to the settings pages' sentence. */
  message?: ReactNode;
  saving?: boolean;
  canSave?: boolean;
  onKeepEditing: () => void;
  onDiscard: () => void;
  onSave: () => void | Promise<void>;
  returnFocusTo?: HTMLElement | null;
}) {
  return (
    <Modal
      open={open}
      onClose={onKeepEditing}
      title="Unsaved changes"
      trapFocus
      returnFocusTo={returnFocusTo}
      closeDisabled={saving}
      closeDisabledReason="Saving your changes…"
      footer={
        // Wraps on a phone: three buttons side by side need more than its width.
        <div className="flex w-full flex-wrap justify-end gap-2">
          <Button variant="secondary" onClick={onKeepEditing} disabled={saving}>
            Keep editing
          </Button>
          <Button variant="secondary" onClick={onDiscard} disabled={saving}>
            Discard changes
          </Button>
          <Button onClick={() => void onSave()} loading={saving} disabled={!canSave}>
            Save and continue
          </Button>
        </div>
      }
    >
      <div className="text-sm text-ink-label">{message}</div>
    </Modal>
  );
}
